import { useTranslation } from "react-i18next";
import { BASE_URL, PATHS } from "common/constants";
import arc1 from "assets/icons/home/hmArc1.svg";
import arc2 from "assets/icons/home/hmArc2.svg";
import arc3 from "assets/icons/home/hmArc3.svg";
import arc4 from "assets/icons/home/hmArc4.svg";
import arc5 from "assets/icons/home/hmArc5.svg";
import arc6 from "assets/icons/home/hmArc6.svg";
import coreLogo from "assets/icons/home/hmCoreLogo.svg";
import coreLogoGlow from "assets/icons/home/hmCoreLogoGlow.svg";
import chipRing from "assets/icons/home/hmChipRing.svg";
import chipDisc from "assets/icons/home/hmChipDisc.svg";
import dot from "assets/icons/home/hmDot.svg";
import dotGlow from "assets/icons/home/hmDotGlow.svg";
import iconUsers from "assets/icons/home/hmIconUsers.svg";
import iconBook from "assets/icons/home/hmIconBook.svg";
import iconWallet from "assets/icons/home/hmIconWallet.svg";
import iconMonitor from "assets/icons/home/hmIconMonitor.svg";
import rayTl from "assets/icons/home/hmRayTl.svg";
import rayTlGlow from "assets/icons/home/hmRayTlGlow.svg";
import rayBl from "assets/icons/home/hmRayBl.svg";
import rayBlGlow from "assets/icons/home/hmRayBlGlow.svg";
import rayTr from "assets/icons/home/hmRayTr.svg";
import rayTrGlow from "assets/icons/home/hmRayTrGlow.svg";
import rayBr from "assets/icons/home/hmRayBr.svg";
import rayBrGlow from "assets/icons/home/hmRayBrGlow.svg";
import HomeButton from "./HomeButton";
import HomeSectionHead from "./HomeSectionHead";
import classes from "./HomeAdvantages.module.scss";

// Преимущества приходят с бэкенда; тексты макета — запасные.
const FALLBACK = [
  {
    title: "Преподаватели — практики",
    thesis:
      "В рамках одного курса вас будет обучать не один преподаватель, а сразу несколько! " +
      "Все они — действующие специалисты по своему профилю и проходят тщательный отбор " +
      "и регулярное повышение квалификации.",
  },
  {
    title: "Менторство",
    thesis:
      "Во время обучения, помимо преподавателей, вам будет доступна персональная поддержка " +
      "менторов, которые помогут решить любые проблемы, связанные с учёбой. Вы также можете " +
      "стать ментором со второго месяца обучения.",
  },
  {
    title: "Возможность заработка",
    thesis:
      "Активные менторы в среднем зарабатывают от 15 000 до 25 000 сом в месяц, тем самым " +
      "покрывая расходы на своё обучение уже во время прохождения курса!",
  },
  {
    title: "Бесплатный повтор",
    thesis:
      "Одна из главных фишек Geeks — возможность перепройти курс повторно абсолютно бесплатно! " +
      "Закрепляйте пройденные темы сколько вам угодно, чтобы добиться поставленной цели!",
  },
];

// Четыре позиции вокруг центра: сверху-слева, снизу-слева, сверху-справа, снизу-справа.
const SPOTS = [
  { id: "tl", icon: iconUsers, delay: 0 },
  { id: "bl", icon: iconBook, delay: 150 },
  { id: "tr", icon: iconWallet, delay: 300 },
  { id: "br", icon: iconMonitor, delay: 450 },
];

// Лучи и точки расставлены по координатам макета: доли от рамки 1440×533.
const RAYS = [
  {
    id: "tl",
    glow: rayTlGlow,
    line: rayTl,
    g: [32.08, 16.6, 12.53],
    l: [33.13, 19.51, 10.42],
  },
  {
    id: "bl",
    glow: rayBlGlow,
    line: rayBl,
    g: [33.19, 73.23, 8.59],
    l: [33.33, 73.97, 8.17],
  },
  {
    id: "tr",
    glow: rayTrGlow,
    line: rayTr,
    g: [57.29, 29.08, 10.07],
    l: [58.71, 32.93, 7.26],
  },
  {
    id: "br",
    glow: rayBrGlow,
    line: rayBr,
    g: [58.17, 65.4, 9.2],
    l: [59.58, 69.23, 6.39],
  },
];

const DOTS = [
  [32.78, 18.76],
  [43.13, 31.71],
  [32.78, 80.68],
  [41.04, 73.17],
  [58.47, 38.46],
  [65.63, 32.27],
  [59.31, 68.48],
  [65.63, 73.17],
];

const ARCS = [arc1, arc2, arc3, arc4, arc5, arc6];

const ROW_PARTNER = { tl: "tr", tr: "tl", bl: "br", br: "bl" };

const withBackendHost = (src) =>
  src?.startsWith("/") ? `${BASE_URL}${src}` : src;

const HomeAdvantages = ({ data }) => {
  const { t } = useTranslation();
  const source = data?.length ? data : FALLBACK;
  const items = SPOTS.slice(0, source.length).map((spot, i) => ({
    ...spot,
    ...source[i],
    icon: withBackendHost(source[i]?.icon) || spot.icon,
  }));
  const hasPartner = (id) => items.some((item) => item.id === ROW_PARTNER[id]);

  return (
    <section className={classes.advantages}>
      <div className={classes.container}>
        <HomeSectionHead
          title={t("WhyStudyAtGeeks")}
          action={
            <HomeButton to={PATHS.advantages} shortLabel="Все">
              {t("AllAdvantages")}
            </HomeButton>
          }
        />

        <div className={classes.orbit}>
          {/* Ядро, лучи и точки — один слой: на мобильном он целиком уменьшается. */}
          <div className={classes.stage}>
            {/* Светящееся ядро с логотипом — центр композиции. */}
            <div className={classes.core} data-aos="zoom-in">
              <span className={classes.arcs}>
                {ARCS.map((src, i) => (
                  <img
                    className={`${classes.arc} ${classes[`arc${i + 1}`]}`}
                    src={src}
                    alt=""
                    key={src}
                  />
                ))}
              </span>
              <span className={classes.coreGlow} />
              <img className={classes.coreLogoGlow} src={coreLogoGlow} alt="" />
              <img className={classes.coreLogo} src={coreLogo} alt="Geeks" />
            </div>

            {RAYS.map((ray) => (
              <span className={classes.ray} key={ray.id}>
                <img
                  className={classes.rayGlow}
                  src={ray.glow}
                  alt=""
                  style={{
                    left: `${ray.g[0]}%`,
                    top: `${ray.g[1]}%`,
                    width: `${ray.g[2]}%`,
                  }}
                />
                <img
                  className={classes.rayLine}
                  src={ray.line}
                  alt=""
                  style={{
                    left: `${ray.l[0]}%`,
                    top: `${ray.l[1]}%`,
                    width: `${ray.l[2]}%`,
                  }}
                />
              </span>
            ))}

            {DOTS.map(([left, top]) => (
              <span
                className={classes.dot}
                style={{ left: `${left}%`, top: `${top}%` }}
                key={`${left}-${top}`}
              >
                <img className={classes.dotGlow} src={dotGlow} alt="" />
                <img className={classes.dotCore} src={dot} alt="" />
              </span>
            ))}
          </div>

          {items.map((item) => (
            <div className={classes.item} key={item.id}>
              <span
                className={`${classes.chip} ${classes[`chip-${item.id}`]}`}
                data-aos="zoom-in"
                data-aos-delay={item.delay}
              >
                <img className={classes.chipRing} src={chipRing} alt="" />
                <img className={classes.chipDisc} src={chipDisc} alt="" />
                <img className={classes.chipIcon} src={item.icon} alt="" />
              </span>

              <div
                className={`${classes.txt} ${classes[`txt-${item.id}`]} ${
                  hasPartner(item.id) ? "" : classes.txtWide
                }`}
                data-aos="fade-up"
                data-aos-delay={item.delay}
              >
                <h3 className={classes.txtTitle}>{item.title}</h3>
                <p className={classes.txtBody}>{item.thesis}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HomeAdvantages;
